import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import type { Serial } from '@kronos/domain'
import { repository } from '../data'
import type { InstallTarget } from '../data'

/**
 * Choosing where a component goes.
 *
 * Not a scan chain any more, and that follows from a decision about the
 * physical world: **assemblies carry no label**. You reach an arm by scanning
 * anything inside it, so there is nothing to point a camera at when the arm is
 * the destination. The list is short — the open positions whose type matches —
 * and it is grouped by robot so "the left arm of R-07" reads as one thing.
 *
 * Mismatches are shown separately rather than hidden. §4.2 makes fitting the
 * wrong type a designer's deliberate override with a recorded reason, and a
 * screen that hides the option entirely would send that person to psql.
 */
export function InstallChain({
  componentId,
  componentSerial,
  canOverride,
  onDone,
  onCancel,
}: {
  componentId: string
  componentSerial: Serial
  canOverride: boolean
  onDone: () => void
  onCancel: () => void
}): JSX.Element {
  const [problem, setProblem] = useState<string | null>(null)
  const [deviating, setDeviating] = useState<InstallTarget | null>(null)
  const [reason, setReason] = useState('')

  const targets = useQuery({
    queryKey: ['install-targets', componentId],
    queryFn: () => repository.installTargets(componentId),
  })

  const install = useMutation({
    mutationFn: (input: { target: InstallTarget; reason?: string }) =>
      repository.install(componentSerial, input.target.parentSerial, input.target.position, input.reason),
    onSuccess: () => {
      setProblem(null)
      onDone()
    },
    onError: (e: Error) => setProblem(e.message.replace(/^\d+:\s*/, '')),
  })

  const all = targets.data ?? []
  const matching = all.filter((t) => t.matches)
  const mismatched = all.filter((t) => !t.matches)

  if (deviating) {
    return (
      <div className="card stack">
        <span className="badge warning">This does not match the design</span>
        <p style={{ margin: 0 }}>
          <strong>{label(deviating)}</strong> expects a {deviating.expectedTypeName}. Fitting
          something else is recorded as a deviation against this robot for as long as it is in.
        </p>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why? e.g. field bodge, ECO-14 raised"
          aria-label="Reason for the deviation"
          style={inputStyle}
        />
        <div className="row">
          <button
            className="primary"
            disabled={reason.trim() === '' || install.isPending}
            onClick={() => install.mutate({ target: deviating, reason })}
          >
            Record and install
          </button>
          <button onClick={() => { setDeviating(null); setReason('') }}>Back</button>
        </div>
        {problem ? <p className="badge critical">{problem}</p> : null}
      </div>
    )
  }

  return (
    <div className="card stack">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <strong>
          Install <span className="serial">{componentSerial}</span>
        </strong>
        <button onClick={onCancel} style={{ minHeight: 36 }}>Cancel</button>
      </div>

      {targets.isPending ? <span className="muted">Finding somewhere it fits…</span> : null}

      {!targets.isPending && matching.length === 0 ? (
        <span className="badge warning">
          Nothing has a free position expecting this type. Build or take in the assembly it goes
          into first.
        </span>
      ) : null}

      <div className="stack">
        {matching.map((t) => (
          <button
            key={`${t.parentId}-${t.position}`}
            onClick={() => install.mutate({ target: t })}
            disabled={install.isPending}
            style={{ justifyContent: 'flex-start', textAlign: 'left', height: 'auto', padding: 12 }}
          >
            <span style={{ display: 'block' }}>
              {label(t)}
              <span className="muted" style={{ display: 'block', fontSize: 'var(--font-size-sm)' }}>
                <span className="serial">{t.parentSerial}</span> · expects {t.expectedTypeName}
              </span>
            </span>
          </button>
        ))}
      </div>

      {canOverride && mismatched.length > 0 ? (
        <details>
          <summary className="muted" style={{ cursor: 'pointer', minHeight: 36, display: 'flex', alignItems: 'center' }}>
            {mismatched.length} position{mismatched.length > 1 ? 's' : ''} that expect something else
          </summary>
          <div className="stack" style={{ marginTop: 8 }}>
            {mismatched.slice(0, 20).map((t) => (
              <button
                key={`${t.parentId}-${t.position}`}
                onClick={() => setDeviating(t)}
                style={{ justifyContent: 'flex-start', textAlign: 'left', height: 'auto', padding: 12 }}
              >
                <span style={{ display: 'block' }}>
                  {label(t)}
                  <span className="muted" style={{ display: 'block', fontSize: 'var(--font-size-sm)' }}>
                    expects {t.expectedTypeName}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </details>
      ) : null}

      {problem ? <p className="badge critical">{problem}</p> : null}
    </div>
  )
}

/**
 * "R-07 · arm_left.joint_3" on a robot, "Arm 0002E · joint_3" on a loose
 * assembly.
 *
 * The path matters: a robot with two arms has two free `joint_3`s, and a label
 * that says only "R-07 · joint_3" twice is a coin toss with a serialized part
 * on the end of it.
 */
function label(t: InstallTarget): string {
  const where = t.parentPath ? `${t.parentPath}.${t.position}` : t.position
  return t.rootFleetNumber ? `${t.rootFleetNumber} · ${where}` : `${t.parentLabel} · ${t.position}`
}

const inputStyle = {
  minHeight: 'var(--tap)',
  padding: '0 12px',
  border: '1px solid var(--border-strong)',
  borderRadius: 'var(--radius)',
  background: 'var(--bg-sunken)',
} as const
